import React, { useEffect } from 'react'
import CardTable from '../../../../components/common/CardTable'
import Table from '../../../../components/common/Table/Table'
import Pagination from '../../../../components/common/Table/Pagination'
import { useNavigate } from 'react-router-dom'
import ScrollToTopBtn from '../../../../components/common/ScrollToTopBtn'
import Loader from '../../../../components/common/Loader'
import Notifications from '../../../../components/common/Notifications'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { fetchPastApplications, setPastCurrentPage } from '../../../../store/slices/Tuition/tuitionManagerPortalSlice'
import { trackButtonClick } from '../../../../services/analytics'
import { safeGet } from '../../../../utils/safeGet'

const PastApplications = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const pastState = useAppSelector(
    (s: any) => s?.tuitionManagerPortal?.pastApplications
  )
  const { applications = [], currentPage = 1, perPage = 10, totalItems = 0, loading = false, error = null } = safeGet(pastState, {})

  interface TableColumn {
    key: string
    label: string
    className?: string
  }

  interface TableRow {
    [key: string]: React.ReactNode
  }

  const columns: TableColumn[] = [
    { key: 'empNuid', label: 'Employee ID' },
    { key: 'empName', label: 'Name' },
    { key: 'id', label: 'Application #' },
    { key: 'applicationDate', label: 'Application date' },
    { key: 'status', label: 'Status' },
    { key: 'approveDate', label: 'Approval Date' },
    { key: 'action', label: 'View' },
  ]

  const profileLoading = useAppSelector((s: any) => s.profile.loading)
  const profile = useAppSelector((s: any) => s.profile.profile)
  const profileError = useAppSelector((s: any) => s.profile.profileError)
  const displayError = profileError || error
  console.log('👤 Profile data in PastApplications:', profile, 'Loading:', profileLoading   )

  useEffect(() => {

    if (profile?.isManager) {
      dispatch(fetchPastApplications({ page: currentPage, per_page: perPage }))
    }
  }, [currentPage, perPage, dispatch, profile, profileLoading])

  const handlePageChange = (page: number) => {
    dispatch(setPastCurrentPage(page))
  }

  const handleActionClick = (row: any) => {
    const id = row?.id ?? ''
    const empNuid = row?.empNuid ?? ''
    if (id && empNuid) {
      console.log(`📊 Analytics: View details clicked - Past Application ${id}`)
      trackButtonClick('View details', 'tuition-past-applications', { applicationId: id })
      // trackCustomPageView(`Tuition Manager Details - Application ${id}`)
      navigate(`/tuition/manager-details/${encodeURIComponent(String(id))}`, {
        state: { empNuid },
      })
    }
  }



  const rows: TableRow[] = (Array.isArray(applications) ? applications : []).map((r) => ({
    ...r,
    status: (
      <span className="bg-gray-100 border border-[#00000014] rounded px-3! py-1 text-sm font-semibold inline-flex items-center justify-center">
        {r.status}
      </span>
    ),
    action: (
      <span className="inline-link medium">View details</span>
    ),
  }))

  // Show loader while profile or past-applications are loading
  if (profileLoading || loading) {
    return (
      <div className="page-container">
        <CardTable>
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader />
          </div>
        </CardTable>
      </div>
    );
  }

  // If profile exists and user is not a manager, show Access denied (no table)
  if (profile && !profile.isManager) {
    return (
      <div className="page-container">
        <CardTable>
          <div className="w-full flex items-center justify-center min-h-[300px]">
            <div className="w-full max-w-md">
              <div className="flex flex-col items-center text-center">
                <h3 className="h3 text-red-600 mb-2">Access denied</h3>
                <p className="text-gray-700 text-sm">You do not have permission to view the Manager Portal.</p>
              </div>
            </div>
          </div>
        </CardTable>
        <ScrollToTopBtn />
      </div>
    )
  }

  if (displayError) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Notifications messageInfo={displayError} status={false} />
      </div>
    )
  }

  // If there are no past applications, show friendly empty state
  if (Array.isArray(applications) && applications.length === 0) {
    return (
      <div className="page-container">
        <CardTable>
          <div className="w-full flex items-center justify-center min-h-[400px]">
            <div className="w-full max-w-md">
              <div className="flex flex-col items-center text-center">
                <h3 className="h3 text-gray-600 mb-2">No applications found</h3>
              </div>
            </div>
          </div>
        </CardTable>
        <ScrollToTopBtn />
      </div>
    )
  }

  return (
    <div className="page-container">
      <CardTable>
        <Table columns={columns} rows={rows} caption="Past tuition applications" isSorting={false} onActionClick={handleActionClick} />
        <div className="mt-4 mobile:relative mobile:left-[30px]">
          <Pagination
            totalItems={safeGet(totalItems, applications.length)}
            itemsPerPage={perPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      </CardTable>

      <ScrollToTopBtn />
    </div>
  )
}

export default PastApplications
